import type { MeasureDateRange } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  StatementDataColumnConfiguration,
  StatementLineConfiguration,
} from "../../../../../../../../api/types/svTypes.ts";

type PeriodLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level">;
type PeriodColumn = Pick<StatementDataColumnConfiguration, "dateRange" | "periodScope" | "periodLineGuids">;

const isPeriodPickableLine = (line: Pick<StatementLineConfiguration, "lineType">): boolean =>
  line.lineType === "Amount" || line.lineType === "Dimension" || line.lineType === "Group";

export const getPeriodPickableLines = <T extends PeriodLine>(lines: T[]): T[] => lines.filter(isPeriodPickableLine);

export const getLivePeriodLineGuids = (
  column: Pick<PeriodColumn, "periodLineGuids">,
  lines: PeriodLine[]
): string[] => {
  const pickableGuids = new Set(getPeriodPickableLines(lines).map((line) => line.guid));
  return (column.periodLineGuids ?? []).filter((guid) => pickableGuids.has(guid));
};

export const remapBrokenDownPeriodGuids = (previousLines: PeriodLine[], lines: PeriodLine[], guids: string[]): string[] =>
  guids.map((guid) => {
    const index = lines.findIndex((line) => line.guid === guid);
    const subtotal = lines[index];
    if (previousLines.find((line) => line.guid === guid)?.lineType !== "Amount" || subtotal?.lineType !== "Subtotal") {
      return guid;
    }
    return lines.slice(0, index).filter((line) => line.lineType === "Group" && line.level === subtotal.level).pop()?.guid ?? guid;
  });

export const periodCoversLine = (lines: PeriodLine[], selectedGuids: string[], lineGuid: string): boolean => {
  const index = lines.findIndex((line) => line.guid === lineGuid);
  const line = lines[index];
  if (!line || selectedGuids.length === 0) {
    return false;
  }
  if (selectedGuids.includes(lineGuid)) {
    return true;
  }
  let level = line.level;
  for (let i = index - 1; i >= 0 && level > 0; i--) {
    const ancestor = lines[i];
    if (ancestor && ancestor.level < level) {
      if (selectedGuids.includes(ancestor.guid)) {
        return true;
      }
      level = ancestor.level;
    }
  }
  return false;
};

export const getColumnPeriodForLine = (
  column: PeriodColumn,
  lines: PeriodLine[],
  lineGuid: string
): MeasureDateRange | undefined => {
  if (!column.dateRange) {
    return undefined;
  }
  if (column.periodScope !== "Selected") {
    return column.dateRange;
  }
  return periodCoversLine(lines, getLivePeriodLineGuids(column, lines), lineGuid) ? column.dateRange : undefined;
};
